import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  Inject,
  SimpleChanges,
} from '@angular/core';
import {
  entityIdFieldName,
  editableFieldName,
} from 'app/shared/constants/search-results.constant';
import { MainMapService } from 'app/shared/components/main-map/main-map.service';
import {
  BehaviorSubject,
  catchError,
  combineLatest,
  distinctUntilChanged,
  finalize,
  map,
  Observable,
  of,
  shareReplay,
  Subject,
  switchMap,
  takeUntil,
  tap,
} from 'rxjs';
import { AuthService } from 'app/shared/services/auth.service';
import { RoleType } from 'app/shared/models/role-type';
import { WidgetModalType } from 'app/shared/models/widget-modal-type';
import { EventBusService } from 'app/shared/services/event-bus.service';
import { onSubscribe } from '@rxjs-lifecycle/core';
import { LoadingController } from 'app/shared/helpers/loading.controller';
import Viewpoint from '@arcgis/core/Viewpoint';
import { SiiaAttachmentLike } from 'app/shared/services/siia-attachments.service';
import { ToastService } from 'app/shared/services/toast.service';
import { SEARCH_LOCAL_LOADING } from '../../search-results.component';
import { SearchResultsService } from '../../search-results.service';
import { ScreenService } from 'app/shared/services/screen.service';
import { EsriUtils } from 'app/shared/utils/esri.utils';
import { TranslationService } from 'app/shared/services/translation.service';

export interface ResultActionEvent {
  index: number;
  item: __esri.Graphic;
}

@Component({
  selector: 'app-results-table-view',
  templateUrl: 'results-table-view.component.html',
  styleUrls: ['results-table-view.component.scss'],
})
export class ResultsTableViewComponent implements OnInit, OnChanges, OnDestroy {
  resultList: __esri.Graphic[] = [];
  fields: __esri.Field[] = [];

  fieldsOrdered: __esri.Field[] = [];

  private _currentPageIndex: number;

  @Input()
  pageIndex = 0;
  @Output()
  pageIndexChange = new EventEmitter<number>(true);

  @Output()
  onGraphicZoomed = new EventEmitter<ResultActionEvent>();

  displayedColumns: string[];

  _pagedData: any[] = [];
  pageSize = 9;

  totalCountObservable = this.searchResultsService.observeSearchTotalCount();

  private zoomActiveElementIndex: number;
  private editActiveElementIndex: number;

  private _documentsMap = new Map<any, Observable<SiiaAttachmentLike[]>>();
  private _hasDocumentMap = new Map<any, Observable<boolean>>();

  private _canEditItemObservableMap = new Map<
    __esri.Graphic,
    Observable<boolean>
  >();

  private pageIndexInternalSubject = new BehaviorSubject<number>(0);

  isMobileMode: boolean;

  private lifecycle = new Subject<void>();

  constructor(
    public searchResultsService: SearchResultsService,
    private mainMapService: MainMapService,
    private authService: AuthService,
    private eventBus: EventBusService,
    private toastService: ToastService,
    @Inject(SEARCH_LOCAL_LOADING) private loadingController: LoadingController,
    private globalLoadingController: LoadingController,
    private screenService: ScreenService,
    public translationService: TranslationService // Adicionado para uso no template
  ) {}

  ngOnInit() {
    this.screenService
      .observeIsMobile()
      .pipe(takeUntil(this.lifecycle))
      .subscribe((isMobile) => {
        this.isMobileMode = isMobile;
      });

    this.eventBus
      .on('register-element.edit-finish')
      .pipe(takeUntil(this.lifecycle))
      .subscribe(() => {
        this.editActiveElementIndex = null;
      });

    this.searchResultsService.onFieldsLoaded
      .pipe(takeUntil(this.lifecycle))
      .subscribe((fields) => {
        this.fields = fields ?? [];

        this.handleFields();
      });

    combineLatest(
      this.searchResultsService.observeSearchFeatures().pipe(
        tap(() => {
          this.reset();
        })
      ),
      this.pageIndexInternalSubject.pipe(distinctUntilChanged())
    )
      .pipe(takeUntil(this.lifecycle))
      .subscribe(([res, pageIndex]) => {
        this.resultList = res ?? [];

        this.onPageIndexChange(pageIndex);
      });
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.pageIndex) {
      this.pageIndexInternalSubject.next(changes.pageIndex?.currentValue ?? 0);
    }
  }

  ngOnDestroy(): void {
    this.lifecycle.next();
    this.lifecycle.complete();

    this.reset();
  }

  onPageIndexChange(pageIndex?: number) {
    pageIndex = Math.max(pageIndex ?? 0, 0);

    const lastPageIndex =
      Math.ceil((this.resultList?.length ?? 0) / this.pageSize) - 1 || 0;
    const newPageIndex = Math.min(pageIndex, lastPageIndex);

    if (this._currentPageIndex === newPageIndex) {
      return;
    }
    this._currentPageIndex = newPageIndex;

    const start = newPageIndex * this.pageSize;

    this._pagedData =
      this.resultList
        ?.slice(start, start + this.pageSize)
        ?.map((x) => x.attributes) ?? [];

    if (this.pageIndex !== newPageIndex) {
      this.pageIndex = newPageIndex;
      this.pageIndexChange.emit(this.pageIndex);
    }
  }

  onBtnEditClick(dataIndex: number) {
    const index = dataIndex + this.pageIndex * this.pageSize;
    const item = this.resultList[index];
    this.mainMapService.openWidgetModal(WidgetModalType.RegisterElement);

    this.eventBus.emit('register-element.edit-start', item);

    this.editActiveElementIndex = index;
  }

  onBtnZoomToClick(dataIndex: number) {
    if (this.globalLoadingController.isShown()) {
      return;
    }

    const index = this.dataIndexToElementIndex(dataIndex);
    const item = this.resultList[index];

    this.globalLoadingController.show();
    this.searchResultsService
      .getFeatureGeometry(item)
      .pipe(
        finalize(() => {
          this.globalLoadingController.hide();
        }),
        takeUntil(this.lifecycle)
      )
      .subscribe({
        next: (geometry) => {
          item.geometry = geometry;

          this.zoomActiveElementIndex = index;

          if (geometry) {
            if (geometry.type === 'point') {
              this.mainMapService.goTo(
                new Viewpoint({
                  targetGeometry: item?.geometry,
                  scale: 524,
                })
              );
            } else {
              this.mainMapService.goTo(geometry);
            }
          }

          this.onGraphicZoomed.emit({
            index,
            item,
          });
        },
        error: (response) => {
          if (!response) {
            return;
          }

          this.toastService.error(
            this.translationService.getCurrentLanguage()['failed-load-data']
          );
        },
      });
  }

  onBtnDownloadDocClick(dataIndex: number) {
    if (this.globalLoadingController.isShown()) {
      return;
    }

    this.globalLoadingController.show();
    this.getDocumentsObservable(dataIndex)
      .pipe(
        finalize(() => {
          this.globalLoadingController.hide();
        }),
        takeUntil(this.lifecycle)
      )
      .subscribe({
        next: (list) => {
          list.forEach((item) => {
            // Abre a URL normalmente, mas para exibir o label traduzido, use translationService.getCurrentLanguage()['url'] onde for exibido
            window.open(item.url, '_blank');
          });
        },
      });
  }

  canEditItemObservable(dataIndex: number) {
    const index = dataIndex + this.pageIndex * this.pageSize;
    const item = this.resultList[index];

    let observable: Observable<boolean>;
    if (this._canEditItemObservableMap.has(item)) {
      observable = this._canEditItemObservableMap.get(item);
    } else {
      if (!item.attributes[editableFieldName]) {
        observable = of(false);
      } else {
        observable = this.authService.observeAuthInfo().pipe(
          map((authInfo) => {
            return authInfo?.entityId === item.attributes[entityIdFieldName];
          }),
          switchMap((aBool) => {
            if (!aBool) {
              return of(aBool);
            }

            return this.authService.observeHasAnyRoleTypes(RoleType.Provider);
          }),
          shareReplay({
            bufferSize: 1,
            refCount: true,
          }),
          distinctUntilChanged()
        );
      }

      this._canEditItemObservableMap.set(item, observable);
    }

    return observable;
  }

  hasDocumentsObservable(dataIndex: number) {
    const index = dataIndex + this.pageIndex * this.pageSize;
    const graphic = this.resultList[index];

    const entityId = graphic.attributes[entityIdFieldName];
    const cdt = graphic.attributes['cdt'];

    const key = `entityId: ${entityId} | cdt: ${cdt}`;
    let observable: Observable<boolean>;
    if (this._hasDocumentMap.has(key)) {
      observable = this._hasDocumentMap.get(key);
    } else {
      observable = this.getDocumentsObservable(dataIndex).pipe(
        map((list) => {
          return !!list.length;
        })
      );
      this._hasDocumentMap.set(key, observable);
    }

    return observable;
  }

  getDocumentsObservable(dataIndex: number) {
    const index = dataIndex + this.pageIndex * this.pageSize;
    const graphic = this.resultList[index];

    const entityId = graphic.attributes[entityIdFieldName];
    const cdt = graphic.attributes['cdt'];

    const key = `entityId: ${entityId} | cdt: ${cdt}`;

    let observable: Observable<SiiaAttachmentLike[]>;
    if (this._documentsMap.has(key)) {
      observable = this._documentsMap.get(key);
    } else {
      observable = this.searchResultsService
        .getAttachmentsFromRegisterElementCDT(graphic)
        .pipe(
          onSubscribe(() => {
            this.loadingController.show();
          }),
          finalize(() => {
            this.loadingController.hide();
          }),
          catchError((response) => {
            if (response) {
              this.toastService.error(
                this.translationService.getCurrentLanguage()[
                  'failed-load-documents'
                ]
              );
            }

            return of([]);
          }),
          shareReplay({
            bufferSize: 1,
            refCount: true,
          })
        );

      this._documentsMap.set(key, observable);
    }

    return observable;
  }

  isBtnZoomActive(dataIndex: number) {
    const index = this.dataIndexToElementIndex(dataIndex);
    return index === this.zoomActiveElementIndex;
  }

  isBtnEditActive(dataIndex: number) {
    const index = this.dataIndexToElementIndex(dataIndex);
    return index === this.editActiveElementIndex;
  }

  private reset() {
    this._hasDocumentMap.clear();
    this._documentsMap.clear();
    this._canEditItemObservableMap.clear();
    this._currentPageIndex = null;
    this.zoomActiveElementIndex = null;
  }

  private dataIndexToElementIndex(dataIndex: number) {
    return dataIndex + this.pageIndex * this.pageSize;
  }

  private handleFields() {
    const fields = this.fields ?? [];

    this.fieldsOrdered = [
      'idobjeto',
      'distrito',
      'concelho',
      'freguesia',
      'arruamento',
      'npolicia',
      'tipoobjecto',
      'afetacaoprincipal',
      'nomeentidade',
      'tipoentidade',
      'largura',
      'altura',
      'comprimento',
      'diametro',
      'cota',
      'baseinstalacao',
      'tracado',
      'tipoutilizacao',
      'estadooperacional',
      'estadoocupacao',
      'created_date',
      'last_edited_date',
    ]
      .map((name) => {
        const field = fields.find((x) => x.name === name);
        return field;
      })
      .filter((x) => !!x);

    this.displayedColumns = [
      'actions',
      ...this.fieldsOrdered.map((x) => x.name),
    ];
  }
}
